import React, { useEffect, useState, useRef } from "react";
import { useAxiosInstance } from "../../../services/Interceptor";
import PayWidget from "../../../components/PayWidget";
import {
  GetEventCombinePrice,
  GetEventsByEventId,
  RegisterAthlete,
} from "../../../services/EventsService";
import { PostPayment } from "../../../services/PaymentService";
import { useParams, useNavigate } from "react-router-dom";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { getCouponPrice } from "../../../services/PublicServices";
import couponTypes from "../../../enums/coupon-types";

export default function ModalIntelliPay(props) {
  const apiInstance = useAxiosInstance();
  const { eventId: routeEventId } = useParams();

  const eventId = routeEventId ? parseInt(routeEventId, 10) : props?.eventId;
  const navigate = useNavigate();
  const { user } = useAuthUser() || {};

  const buttonRef = useRef(null);

  const [combinePrice, setCombinePrice] = useState(0);
  const [positionDrillPrice, setPositionDrillPrice] = useState(0);
  const [hasPositionDrills, setHasPositionDrills] = useState(false);
  const [eventInfo, setEventInfo] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPricingLoaded, setIsPricingLoaded] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [discountDetails, setDiscountDetails] = useState({
    combine: {
      discountAmount: 0,
      basePercentage: 0,
      discountType: "",
    },
    positionDrill: {
      discountAmount: 0,
      basePercentage: 0,
      discountType: "",
    },
  });
  const [appliedCouponType, setAppliedCouponType] = useState(null);

  const isModalOpen = true;

  const formatPrice = (value) => `$${Number(value || 0).toFixed(2)}`;
  const getPosDrillPrice = () =>
    hasPositionDrills ? Number(positionDrillPrice || 0) : 0;
  const getCombineTotal = () => {
    const base = Number(combinePrice || 0);
    return Math.max(
      base - Number(discountDetails.combine.discountAmount || 0),
      0,
    );
  };

  const getCombinePositionTotal = () => {
    if (!hasPositionDrills) return getCombineTotal();

    const base = Number(combinePrice || 0) + getPosDrillPrice();
    return Math.max(
      base - Number(discountDetails.positionDrill.discountAmount || 0),
      0,
    );
  };

  const couponAppliesToCombine =
    appliedCouponType === "Combine" ||
    appliedCouponType === "CombineAndPositionDrills";

  const couponAppliesToCombinePos =
    appliedCouponType === "Combine" ||
    appliedCouponType === "PositionDrills" ||
    appliedCouponType === "CombineAndPositionDrills";

  const redirectAfterPayment = () => {
    const { page } = props;

    const routeMap = {
      DashboardEvent: "/athlete/dashboard",
      AthleteEvent: "/athlete/events",
      SignInEvent: "/events",
    };

    const path = routeMap[page] || "/athlete/dashboard";

    setTimeout(() => {
      window.location.href = path;
    }, 3000);
  };

  const closePriceOptions = () => {
    setSelectedType(null);
    setLoading(false);
    setCouponCode("");
    setDiscountDetails({
      combine: { discountAmount: 0, basePercentage: 0, discountType: "" },
      positionDrill: { discountAmount: 0, basePercentage: 0, discountType: "" },
    });
    setAppliedCouponType(null);

    if (typeof props.onCloseAll === "function") {
      props.onCloseAll();
      return;
    }

    if (props.origin === "DashboardEvent") {
      navigate("/athlete/dashboard/");
      return;
    }

    navigate(-1);
  };

  useEffect(() => {
    setSelectedType(null);
    setLoading(false);
    setCouponCode("");
    setDiscountDetails({
      combine: { discountAmount: 0, basePercentage: 0, discountType: "" },
      positionDrill: { discountAmount: 0, basePercentage: 0, discountType: "" },
    });
    setAppliedCouponType(null);
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, eventId);
        if (data) {
          setEventInfo(`${data.date} - ${data.name} - ${data.city}`);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadEvents();
  }, [apiInstance, eventId]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const combinePriceRes = await GetEventCombinePrice(
          apiInstance,
          eventId,
        );
        setCombinePrice(combinePriceRes?.data?.combinePrice || 0);
        setHasPositionDrills(!!combinePriceRes?.data?.hasPositionDrills);
        setPositionDrillPrice(combinePriceRes?.data?.positionDrillPrice);
        setIsPricingLoaded(true);
      } catch (error) {
        console.error("Error fetching pricing:", error);
        setIsPricingLoaded(true);
      }
    };

    fetchPricing();
  }, [apiInstance, eventId]);

  const applyCouponCode = async () => {
    try {
      localStorage.removeItem("appliedCouponCode");

      const response = await getCouponPrice(apiInstance, couponCode);

      if (response.type === couponTypes.invalid) {
        setDiscountDetails({
          combine: { discountAmount: 0, basePercentage: 0, discountType: "" },
          positionDrill: {
            discountAmount: 0,
            basePercentage: 0,
            discountType: "",
          },
        });
        setAppliedCouponType(null);
        toast.error("Invalid coupon code", {
          containerId: "modal",
          position: "top-right",
        });
        return;
      }

      if (
        response.type === couponTypes.subscription ||
        (response.type === couponTypes.positionDrills && !hasPositionDrills)
      ) {
        setDiscountDetails({
          combine: { discountAmount: 0, basePercentage: 0, discountType: "" },
          positionDrill: {
            discountAmount: 0,
            basePercentage: 0,
            discountType: "",
          },
        });
        setAppliedCouponType(null);
        toast.error("Subscription Coupons are not valid for this purchase", {
          containerId: "modal",
          position: "top-right",
        });
        return;
      }

      const amount = Number(response.amount || 0);
      const type = String(response.type || "").trim();
      const discountType = String(response.discountType || "").trim();

      const combineBase = Number(combinePrice || 0);
      const combinePosBase =
        combineBase + (hasPositionDrills ? getPosDrillPrice() : 0);

      const calcDiscount = (baseAmount) => {
        if (discountType === "Percent Off") {
          return Math.min((baseAmount * amount) / 100, baseAmount);
        }

        return Math.min(amount, baseAmount);
      };

      let newDiscountDetails = {
        combine: {
          discountAmount: 0,
          basePercentage: 0,
          discountType,
        },
        positionDrill: {
          discountAmount: 0,
          basePercentage: 0,
          discountType,
        },
      };

      if (type === "Combine") {
        newDiscountDetails.combine.discountAmount = calcDiscount(combineBase);
        newDiscountDetails.positionDrill.discountAmount =
          calcDiscount(combineBase);

        if (discountType === "Percent Off") {
          newDiscountDetails.combine.basePercentage = amount;
          newDiscountDetails.positionDrill.basePercentage = amount;
        }

        setAppliedCouponType("Combine");
      } else if (type === "PositionDrills") {
        if (!hasPositionDrills) {
          setDiscountDetails({
            combine: { discountAmount: 0, basePercentage: 0, discountType: "" },
            positionDrill: {
              discountAmount: 0,
              basePercentage: 0,
              discountType: "",
            },
          });
          setAppliedCouponType(null);
          toast.error("Position Drills coupon is not valid for this event.", {
            containerId: "modal",
            position: "top-right",
          });
          return;
        }

        newDiscountDetails.combine.discountAmount = 0;
        newDiscountDetails.positionDrill.discountAmount =
          calcDiscount(combinePosBase);

        if (discountType === "Percent Off") {
          newDiscountDetails.positionDrill.basePercentage = amount;
        }

        setAppliedCouponType("PositionDrills");
      } else if (type === "CombineAndPositionDrills") {
        // Left side should always get combine-based discount
        newDiscountDetails.combine.discountAmount = calcDiscount(combineBase);

        // Right side:
        // Percent Off => calculate on combine + position drill total
        // Amount Off => apply same base amount on right side
        if (discountType === "Percent Off") {
          newDiscountDetails.positionDrill.discountAmount =
            calcDiscount(combinePosBase);
          newDiscountDetails.combine.basePercentage = amount;
          newDiscountDetails.positionDrill.basePercentage = amount;
        } else {
          newDiscountDetails.positionDrill.discountAmount = Math.min(
            amount,
            combinePosBase,
          );
        }

        setAppliedCouponType("CombineAndPositionDrills");
      } else {
        setDiscountDetails({
          combine: { discountAmount: 0, basePercentage: 0, discountType: "" },
          positionDrill: {
            discountAmount: 0,
            basePercentage: 0,
            discountType: "",
          },
        });
        setAppliedCouponType(null);
        toast.error("Coupon type not supported for this purchase.", {
          containerId: "modal",
          position: "top-right",
        });
        return;
      }

      setDiscountDetails(newDiscountDetails);

      localStorage.setItem("appliedCouponCode", couponCode);

      toast.success("Coupon applied successfully!", {
        containerId: "modal",
        position: "top-right",
      });
    } catch (error) {
      console.error("Coupon apply error:", error);
      setDiscountDetails({
        combine: { discountAmount: 0, basePercentage: 0, discountType: "" },
        positionDrill: {
          discountAmount: 0,
          basePercentage: 0,
          discountType: "",
        },
      });
      setAppliedCouponType(null);
      toast.error("Invalid coupon code", {
        containerId: "modal",
        position: "top-right",
      });
    }
  };

  const handleCombinePass = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!isPricingLoaded || loading) return;

    const total = getCombineTotal();

    if (total <= 0) {
      try {
        setLoading(true);
        const userId = user?.id;
        const regRes = await RegisterAthlete(
          apiInstance,
          eventId,
          userId,
          props.tShirtSize,
        );

        if (regRes.status === 200) {
          const paymentPayload = {
            userId: user?.id,
            amount: 0,
            isCombine: true,
            isPositionDrill: false,
            isAnnualSubscription: false,
            isMonthlySubscription: false,
            isCash: false,
            eventId,
            transactionId: "N/A",
            intellipayToken: "",
          };

          const paidRes = await PostPayment(apiInstance, paymentPayload);

          if (paidRes.status === 200) {
            toast.success("Registration and payment successful!", {
              containerId: "modal",
              position: "top-right",
            });
            setTimeout(() => redirectAfterPayment(), 1000);
          } else {
            toast.error("Payment after registration failed.", {
              containerId: "modal",
              position: "top-right",
            });
          }
        } else {
          toast.error("Registration failed.", {
            containerId: "modal",
            position: "top-right",
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong.", {
          containerId: "modal",
          position: "top-right",
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    setSelectedType("combine");
    setLoading(true);

    setTimeout(() => {
      if (buttonRef.current) buttonRef.current.click();
      setLoading(false);
    }, 5000);
  };

  const handleCombinePositionDrill = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!hasPositionDrills) return;
    if (!isPricingLoaded || loading) return;

    const total = getCombinePositionTotal();

    if (total <= 0) {
      try {
        const userId = user?.id;
        setLoading(true);
        const regRes = await RegisterAthlete(
          apiInstance,
          eventId,
          userId,
          props.tShirtSize,
        );

        if (regRes.status === 200) {
          const payloads = [
            {
              userId: user?.id,
              amount: 0,
              isCombine: true,
              isPositionDrill: false,
              isAnnualSubscription: false,
              isMonthlySubscription: false,
              isCash: false,
              eventId,
              transactionId: "N/A",
              intellipayToken: "",
            },
            {
              userId: user?.id,
              amount: 0,
              isCombine: false,
              isPositionDrill: true,
              isAnnualSubscription: false,
              isMonthlySubscription: false,
              isCash: false,
              eventId,
              transactionId: "N/A",
              intellipayToken: "",
            },
          ];

          for (const p of payloads) {
            const paidRes = await PostPayment(apiInstance, p);
            if (paidRes.status !== 200) {
              toast.error("Payment after registration failed.", {
                containerId: "modal",
                position: "top-right",
              });
              return;
            }
          }

          toast.success("Registration and payment successful!", {
            containerId: "modal",
            position: "top-right",
          });
          setTimeout(() => redirectAfterPayment(), 1000);
        } else {
          toast.error("Event registration failed.", {
            containerId: "modal",
            position: "top-right",
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong.", {
          containerId: "modal",
          position: "top-right",
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    setSelectedType("combinePosition");
    setLoading(true);

    setTimeout(() => {
      if (buttonRef.current) buttonRef.current.click();
      setLoading(false);
    }, 5000);
  };

  return (
    <>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          {loading && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[100]">
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                size="3x"
                className="text-blue-800"
              />
            </div>
          )}

          <div className="bg-white rounded-md shadow-xl w-full max-w-3xl flex flex-col max-h-[72vh] m-3 relative -mt-16">
            <div className="flex justify-between items-center px-4 py-3">
              <h2 className="text-xl font-bold text-gray-900">
                Portal Combine Registration
                <p className="text-sm text-gray-700 font-normal"></p>
              </h2>

              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-full text-sm p-2 ml-auto inline-flex items-center -mt-4"
                onClick={closePriceOptions}
              >
                <FontAwesomeIcon
                  icon={faXmark}
                  className="cursor-pointer text-lg"
                />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                <div className="flex flex-col justify-between border border-gray-300 p-4 rounded-md">
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="text-xl mr-2">🏈</div>
                      <h3 className="text-xl font-bold text-black">
                        Combine Pass
                      </h3>
                    </div>

                    <div className="pl-0 xl:pl-6">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-700">
                          Combine Price:
                        </span>
                        <span className="text-sm text-black">
                          {formatPrice(combinePrice)}
                        </span>
                      </div>

                      {couponAppliesToCombine &&
                        discountDetails.combine.discountAmount > 0 && (
                          <>
                            {discountDetails.combine.discountType ===
                              "Percent Off" && (
                              <div className="flex justify-between text-green-600 mb-1">
                                <span className="text-sm font-semibold">
                                  Discount Percentage:
                                </span>
                                <span className="text-sm">
                                  {discountDetails.combine.basePercentage}%
                                </span>
                              </div>
                            )}

                            <div className="flex justify-between text-green-600 mb-1">
                              <span className="text-sm font-semibold">
                                Discount Amount:
                              </span>
                              <span className="text-sm">
                                -
                                {formatPrice(
                                  discountDetails.combine.discountAmount,
                                )}
                              </span>
                            </div>
                          </>
                        )}

                      <div className="border-t border-gray-300 pt-2 mt-2">
                        <div className="flex justify-between font-bold text-lg">
                          <span>Total:</span>
                          <span>{formatPrice(getCombineTotal())}</span>
                        </div>
                      </div>

                      <p className="text-sm text-gray-500 mt-3">One-Time</p>
                      <p className="text-sm text-gray-700 mb-2">{eventInfo}</p>

                      <div className="border-t border-gray-300 pt-3">
                        <h4 className="text-sm font-semibold mb-1 text-black">
                          What's included
                        </h4>
                        <ul className="text-sm text-gray-700 list-disc ml-5">
                          <li>Combine Participation</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="mt-5 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleCombinePass}
                    disabled={!isPricingLoaded || loading}
                  >
                    {!isPricingLoaded
                      ? "LOADING..."
                      : getCombineTotal() <= 0
                        ? "REGISTER FOR COMBINE"
                        : "PAY FOR COMBINE"}
                  </button>
                </div>

                {hasPositionDrills && (
                  <div className="flex flex-col justify-between border border-gray-300 p-4 rounded-md ">
                    <div>
                      <div className="flex items-center mb-3">
                        <div className="text-xl mr-2">🏈</div>
                        <h3 className="text-xl font-bold text-black">
                          Combine + Position Drills
                        </h3>
                      </div>

                      <div className="pl-0 xl:pl-6">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-700">
                            Combine Price:
                          </span>
                          <span className="text-sm text-black">
                            {formatPrice(combinePrice)}
                          </span>
                        </div>

                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-700">
                            Position Drill Price:
                          </span>
                          <span className="text-sm text-black">
                            {formatPrice(getPosDrillPrice())}
                          </span>
                        </div>

                        {couponAppliesToCombinePos &&
                          discountDetails.positionDrill.discountAmount > 0 && (
                            <>
                              {discountDetails.positionDrill.discountType ===
                                "Percent Off" && (
                                <div className="flex justify-between text-green-600 mb-1">
                                  <span className="text-sm font-semibold">
                                    Discount Percentage:
                                  </span>
                                  <span className="text-sm">
                                    {
                                      discountDetails.positionDrill
                                        .basePercentage
                                    }
                                    %
                                  </span>
                                </div>
                              )}

                              <div className="flex justify-between text-green-600 mb-1">
                                <span className="text-sm font-semibold">
                                  Discount Amount:
                                </span>
                                <span className="text-sm">
                                  -
                                  {formatPrice(
                                    discountDetails.positionDrill
                                      .discountAmount,
                                  )}
                                </span>
                              </div>
                            </>
                          )}
                        <div className="border-t border-gray-300 pt-2 mt-2">
                          <div className="flex justify-between font-bold text-lg">
                            <span>Total:</span>
                            <span>
                              {formatPrice(getCombinePositionTotal())}
                            </span>
                          </div>
                        </div>

                        <p className="text-sm text-gray-500 mt-3">One-Time</p>

                        <div className="border-t border-gray-300 pt-3">
                          <h4 className="text-sm font-semibold mb-1 text-black">
                            What's included
                          </h4>
                          <ul className="text-sm text-gray-700 list-disc ml-5 space-y-1">
                            <li>Combine Participation</li>
                            <li>Position Drills</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="mt-5 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleCombinePositionDrill}
                      disabled={!isPricingLoaded || loading}
                    >
                      {!isPricingLoaded
                        ? "LOADING..."
                        : getCombinePositionTotal() <= 0
                          ? "REGISTER FOR COMBINE + POSITION DRILL"
                          : "PAY FOR COMBINE + POSITION DRILL"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="border-0 px-4 pb-6 pt-4 flex justify-start gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <label
                  htmlFor="coupon"
                  className="text-sm font-semibold text-black"
                >
                  Coupon Code:
                </label>
                <input
                  type="text"
                  id="coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  placeholder="Enter code"
                />
                <button
                  onClick={applyCouponCode}
                  className="bg-black text-white px-4 py-1 rounded text-sm"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          {selectedType && (
            <>
              <button
                data-ipayname="submit"
                className="ipayfield hidden"
                ref={buttonRef}
              ></button>

              <PayWidget
                key={`paywidget-${selectedType}-${eventId}`}
                userEmail={user?.email}
                userId={user?.id}
                paymentAmount={
                  selectedType === "combine"
                    ? getCombineTotal()
                    : getCombinePositionTotal()
                }
                eventId={eventId}
                subscriptionType={selectedType}
                onRegistrationSuccess={redirectAfterPayment}
                closeAction={true}
                onClose={redirectAfterPayment}
                tShirtSize={props.tShirtSize}
              />
            </>
          )}
        </div>
      )}
    </>
  );
}
